/**
 * Provides API functions for bulk folder permissions management in users management.
 * Backend APIs: GetAllFolders, GetUserFolderPermissions, SetUserFolderPermissions
 */

import { 
    URL_GET_ALL_FOLDERS, 
    URL_GET_USER_FOLDER_PERMISSIONS, 
    URL_SET_USER_FOLDER_PERMISSIONS 
} from 'constants/urls';

/**
 * Gets all folders with role information for new user creation
 * 
 * Returns all directories with "None" permission by default for permission assignment.
 * Used in create mode where user doesn't exist yet.
 * 
 * @returns {Promise<Object>} API response with folders array in Data property
 * @throws {Error} If API call fails or returns Success: false
 */
export const getAllFolders = async () => {
    const options = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    };

    const res = await fetch(URL_GET_ALL_FOLDERS, options);
    const data = await res.json();

    if (!data.Success) {
        throw new Error(data.Message || 'Failed to retrieve all folders');
    }
    
    return data;
};

/**
 * Gets user's current folder permissions for existing user
 * 
 * Returns all directories showing user's actual permissions (None/View/Admin).
 * Used in edit mode where user already exists.
 * 
 * @param {number} userId - The user ID to retrieve folder permissions for
 * @returns {Promise<Object>} API response with user's folder permissions
 * @throws {Error} If API call fails, returns Success: false, or userId is invalid
 */
export const getUserFolderPermissions = async (userId) => {
    if (!userId || userId < 0) {
        throw new Error('Valid user ID is required');
    }

    const options = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    };

    const res = await fetch(URL_GET_USER_FOLDER_PERMISSIONS + userId, options);
    const data = await res.json();

    if (!data.Success) {
        throw new Error(data.Message || 'Failed to retrieve user folder permissions');
    }
    
    return data;
};

/**
 * Sets bulk folder permissions for a user
 * 
 * Replaces user's existing folder permissions with new assignments.
 * Supports None/View/Admin permissions and optional AssignViewWithAdmin flag.
 * Uses "replace" strategy: removes existing permissions, then adds new ones atomically.
 * Note: AdminRoleIds and ViewRoleIds are automatically assigned at Folder creation.
 * 
 * @param {Object} payload - Permission assignment payload
 * @param {number} payload.UserId - The user ID to set permissions for
 * @param {Array} payload.FolderPermissions - Array of folder permission objects
 * @param {number} payload.FolderPermissions[].DirectoryId - Folder ID
 * @param {string} payload.FolderPermissions[].Permission - "None", "View", or "Admin"
 * @param {boolean} [payload.FolderPermissions[].AssignViewWithAdmin=false] - Also assign View role when Admin selected. Defaults to false.
 * @param {number} payload.FolderPermissions[].AdminRoleId - Admin role ID for this folder
 * @param {number} payload.FolderPermissions[].ViewRoleId - View role ID for this folder
 * @returns {Promise<Object>} API response with permission assignment statistics
 * @throws {Error} If API call fails, returns Success: false, or payload is invalid
 */
export const setUserFolderPermissions = async (payload) => {
    if (!payload || !payload.UserId || payload.UserId < 0) {
        throw new Error('Valid user ID is required');
    }

    if (!payload.FolderPermissions || !Array.isArray(payload.FolderPermissions)) {
        throw new Error('FolderPermissions array is required');
    }

    const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        body: JSON.stringify({ payload }) // Note: Backend expects wrapped payload
    };

    const res = await fetch(URL_SET_USER_FOLDER_PERMISSIONS, options);
    const data = await res.json();

    if (!data.Success) {
        throw new Error(data.Message || 'Failed to set user folder permissions');
    }
    
    return data;
};

/**
 * Utility function to prepare folder permissions for API submission
 * 
 * Transforms UI folder data into the format expected by setUserFolderPermissions API.
 * Filters out "None" permissions and adds required role IDs.
 * 
 * @param {Array} folders - Array of folder objects from UI
 * @param {number} userId - User ID for permission assignment
 * @param {boolean} [assignViewWithAdmin=false] - Global flag for hybrid permissions
 * @returns {Object} Formatted payload ready for setUserFolderPermissions
 */
export const prepareFolderPermissionsPayload = (folders, userId, assignViewWithAdmin = false) => {
    if (!folders || !Array.isArray(folders)) {
        throw new Error('Folders array is required');
    }

    if (!userId || userId < 0) {
        throw new Error('Valid user ID is required');
    }

    // Filter out "None" permissions and format for API
    const folderPermissions = folders
        .filter(folder => folder.Permission && folder.Permission !== "None")
        .map(folder => ({
            DirectoryId: folder.DirectoryId,
            Permission: folder.Permission,
            AssignViewWithAdmin: assignViewWithAdmin,
            AdminRoleId: folder.AdminRoleId || -1, // Some old folders may not have AdminRoleId set
            ViewRoleId: folder.ViewRoleId || -1 // Some old folders may not have ViewRoleId set
        }));

    return {
        UserId: userId,
        FolderPermissions: folderPermissions
    };
};

const folderPermissionsApi = {
    getAllFolders,
    getUserFolderPermissions, 
    setUserFolderPermissions,
    prepareFolderPermissionsPayload
};

export default folderPermissionsApi;
